import {
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Stack,
  chakra,
  useControllableProp,
  useFormControlContext,
} from '@chakra-ui/react';
import * as React from 'react';

import { SelectButton } from '@/components/SelectButton';
import { EAttributeAllowedViewerKind } from '@/graphql-types/globalTypes';
import { EAttributeOwnerKind } from '@/graphql-types/globalTypes';
import { usePermissions } from '@/hooks/usePermissions';
import {
  AwardIcon,
  GlobeIcon,
  LockIcon,
  ShieldIcon,
  UsersIcon,
} from '@/imports/ui/chakra/feather';

export enum ALLOWED_VIEWERS_LEVEL {
  ANYONE = 'ANYONE',
  ANY_LOGGED_IN_USER = 'ANY_LOGGED_IN_USER',
  MEMBERS_ONLY = 'MEMBERS_ONLY',
  USER_AND_ADMINS_ONLY = 'USER_AND_ADMINS_ONLY',
  ADMINS_ONLY = 'ADMINS_ONLY',
}

export const allowedViewersLevelToLabel = (
  level: ALLOWED_VIEWERS_LEVEL,
  ownerKind: EAttributeOwnerKind,
  isViewingOwnProfile: boolean
) => {
  return {
    [ALLOWED_VIEWERS_LEVEL.ANYONE]: 'Anyone',
    [ALLOWED_VIEWERS_LEVEL.ANY_LOGGED_IN_USER]: 'Logged In Users',
    [ALLOWED_VIEWERS_LEVEL.MEMBERS_ONLY]: {
      [EAttributeOwnerKind.USER]: isViewingOwnProfile
        ? 'Members'
        : 'Members & The User',
      [EAttributeOwnerKind.ORGANIZATION]: isViewingOwnProfile
        ? `Members & My Company's Admins`
        : `Members & The Company's Admins`,
    }[ownerKind],
    [ALLOWED_VIEWERS_LEVEL.USER_AND_ADMINS_ONLY]: {
      [EAttributeOwnerKind.USER]: isViewingOwnProfile
        ? 'Only Me & Admins'
        : 'The User',
      [EAttributeOwnerKind.ORGANIZATION]: isViewingOwnProfile
        ? `My Company's Admins`
        : `The Company's Admins`,
    }[ownerKind],
    [ALLOWED_VIEWERS_LEVEL.ADMINS_ONLY]: {
      [EAttributeOwnerKind.USER]: 'Only Admins',
      [EAttributeOwnerKind.ORGANIZATION]: 'Only Association Admins',
    }[ownerKind],
  }[level];
};

export const allowedViewersLevelToIcon = (level: ALLOWED_VIEWERS_LEVEL) => {
  return {
    [ALLOWED_VIEWERS_LEVEL.ANYONE]: <GlobeIcon />,
    [ALLOWED_VIEWERS_LEVEL.ANY_LOGGED_IN_USER]: <UsersIcon />,
    [ALLOWED_VIEWERS_LEVEL.MEMBERS_ONLY]: <AwardIcon />,
    [ALLOWED_VIEWERS_LEVEL.USER_AND_ADMINS_ONLY]: <LockIcon />,
    [ALLOWED_VIEWERS_LEVEL.ADMINS_ONLY]: <ShieldIcon />,
  }[level];
};

const _allowedViewersLevelToValue = (level: ALLOWED_VIEWERS_LEVEL) => {
  return {
    [ALLOWED_VIEWERS_LEVEL.ANYONE]: [
      EAttributeAllowedViewerKind.GUEST,
      EAttributeAllowedViewerKind.SELF,
      EAttributeAllowedViewerKind.ASSOCIATION_MEMBER,
      EAttributeAllowedViewerKind.LOGGED_IN_USER,
    ],
    [ALLOWED_VIEWERS_LEVEL.ANY_LOGGED_IN_USER]: [
      EAttributeAllowedViewerKind.SELF,
      EAttributeAllowedViewerKind.ASSOCIATION_MEMBER,
      EAttributeAllowedViewerKind.LOGGED_IN_USER,
    ],
    [ALLOWED_VIEWERS_LEVEL.MEMBERS_ONLY]: [
      EAttributeAllowedViewerKind.SELF,
      EAttributeAllowedViewerKind.ASSOCIATION_MEMBER,
    ],
    [ALLOWED_VIEWERS_LEVEL.USER_AND_ADMINS_ONLY]: [
      EAttributeAllowedViewerKind.SELF,
    ],
    [ALLOWED_VIEWERS_LEVEL.ADMINS_ONLY]: [],
  }[level];
};

export const allowedViewersValueToLevel = (
  value: EAttributeAllowedViewerKind[]
) => {
  if (value.includes(EAttributeAllowedViewerKind.GUEST)) {
    return ALLOWED_VIEWERS_LEVEL.ANYONE;
  }

  if (value.includes(EAttributeAllowedViewerKind.LOGGED_IN_USER)) {
    return ALLOWED_VIEWERS_LEVEL.ANY_LOGGED_IN_USER;
  }

  if (value.includes(EAttributeAllowedViewerKind.ASSOCIATION_MEMBER)) {
    return ALLOWED_VIEWERS_LEVEL.MEMBERS_ONLY;
  }

  if (value.includes(EAttributeAllowedViewerKind.SELF)) {
    return ALLOWED_VIEWERS_LEVEL.USER_AND_ADMINS_ONLY;
  }

  return ALLOWED_VIEWERS_LEVEL.ADMINS_ONLY;
};

export const AllowedViewersSelect = React.forwardRef<
  HTMLButtonElement,
  {
    value: EAttributeAllowedViewerKind[];
    onChange: (value: EAttributeAllowedViewerKind[]) => void;
    isDisabled?: boolean;
    isAdminsOnlyHidden?: boolean;
    ownerKind: EAttributeOwnerKind;
    validAllowedViewers: EAttributeAllowedViewerKind[];
    isViewingOwnProfile: boolean;
  }
>((props, ref) => {
  const permissions = usePermissions();
  const currentLevel = allowedViewersValueToLevel(props.value);

  const formControlContext = useFormControlContext();

  const [, isDisabled] = useControllableProp(
    props.isDisabled ? true : undefined,
    formControlContext?.isDisabled
  );

  return (
    <Menu matchWidth isLazy>
      <MenuButton
        as={SelectButton}
        ref={ref}
        leftIcon={allowedViewersLevelToIcon(currentLevel)}
        isDisabled={isDisabled}
      >
        {allowedViewersLevelToLabel(
          currentLevel,
          props.ownerKind,
          props.isViewingOwnProfile
        )}
      </MenuButton>
      <MenuList>
        {props.validAllowedViewers.includes(
          EAttributeAllowedViewerKind.GUEST
        ) && (
          <MenuItem
            icon={allowedViewersLevelToIcon(ALLOWED_VIEWERS_LEVEL.ANYONE)}
            onClick={() =>
              props.onChange(
                _allowedViewersLevelToValue(ALLOWED_VIEWERS_LEVEL.ANYONE)
              )
            }
          >
            <Stack spacing="0">
              <chakra.span>
                {allowedViewersLevelToLabel(
                  ALLOWED_VIEWERS_LEVEL.ANYONE,
                  props.ownerKind,
                  props.isViewingOwnProfile
                )}
              </chakra.span>
              <chakra.span color="gray.600" fontWeight="normal" fontSize="sm">
                {
                  {
                    [EAttributeOwnerKind.USER]: props.isViewingOwnProfile
                      ? `Anyone who can find my profile can view this attribute, including logged out visitors`
                      : `Anyone who can find the user's profile can view this attribute, including logged out visitors`,
                    [EAttributeOwnerKind.ORGANIZATION]:
                      "Anyone who can find the company's profile can view this attribute, including logged out visitors",
                  }[props.ownerKind]
                }
              </chakra.span>
            </Stack>
          </MenuItem>
        )}
        {props.validAllowedViewers.includes(
          EAttributeAllowedViewerKind.LOGGED_IN_USER
        ) && (
          <MenuItem
            icon={allowedViewersLevelToIcon(
              ALLOWED_VIEWERS_LEVEL.ANY_LOGGED_IN_USER
            )}
            onClick={() =>
              props.onChange(
                _allowedViewersLevelToValue(
                  ALLOWED_VIEWERS_LEVEL.ANY_LOGGED_IN_USER
                )
              )
            }
          >
            <Stack spacing="0">
              <chakra.span>
                {allowedViewersLevelToLabel(
                  ALLOWED_VIEWERS_LEVEL.ANY_LOGGED_IN_USER,
                  props.ownerKind,
                  props.isViewingOwnProfile
                )}
              </chakra.span>
              <chakra.span color="gray.600" fontWeight="normal" fontSize="sm">
                Users within the community can view this attribute
              </chakra.span>
            </Stack>
          </MenuItem>
        )}
        {props.validAllowedViewers.includes(
          EAttributeAllowedViewerKind.ASSOCIATION_MEMBER
        ) && (
          <MenuItem
            icon={allowedViewersLevelToIcon(ALLOWED_VIEWERS_LEVEL.MEMBERS_ONLY)}
            onClick={() =>
              props.onChange(
                _allowedViewersLevelToValue(ALLOWED_VIEWERS_LEVEL.MEMBERS_ONLY)
              )
            }
          >
            {allowedViewersLevelToLabel(
              ALLOWED_VIEWERS_LEVEL.MEMBERS_ONLY,
              props.ownerKind,
              props.isViewingOwnProfile
            )}
          </MenuItem>
        )}
        {props.validAllowedViewers.includes(
          EAttributeAllowedViewerKind.SELF
        ) && (
          <MenuItem
            icon={allowedViewersLevelToIcon(
              ALLOWED_VIEWERS_LEVEL.USER_AND_ADMINS_ONLY
            )}
            onClick={() =>
              props.onChange(
                _allowedViewersLevelToValue(
                  ALLOWED_VIEWERS_LEVEL.USER_AND_ADMINS_ONLY
                )
              )
            }
          >
            {allowedViewersLevelToLabel(
              ALLOWED_VIEWERS_LEVEL.USER_AND_ADMINS_ONLY,
              props.ownerKind,
              props.isViewingOwnProfile
            )}
          </MenuItem>
        )}

        {permissions.canDoAdminStuffAndAuthenticated &&
          !props.isAdminsOnlyHidden && (
            <MenuItem
              icon={allowedViewersLevelToIcon(
                ALLOWED_VIEWERS_LEVEL.ADMINS_ONLY
              )}
              onClick={() =>
                props.onChange(
                  _allowedViewersLevelToValue(ALLOWED_VIEWERS_LEVEL.ADMINS_ONLY)
                )
              }
            >
              {allowedViewersLevelToLabel(
                ALLOWED_VIEWERS_LEVEL.ADMINS_ONLY,
                props.ownerKind,
                props.isViewingOwnProfile
              )}
            </MenuItem>
          )}
      </MenuList>
    </Menu>
  );
});

AllowedViewersSelect.displayName = 'DefaultAllowedViewersSelect';
